import { useNode } from "@craftjs/core";
import React, { useState } from "react";
import {
  Tabs,
  MarginInput,
  PaddingInput,
  UnitInput,
  ColorInput,
  Panel,
  IconInput,
  BorderRadiusInput,
  BorderInput,
  SelectInput,
  BoxShadowInput,
  Spacer,
  ToggleInput,
  NumberInput,
  TabsV2,
  RangeNumberInput,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import { changeTransition, isUpdatedFromDefault } from "../../utils";
import { INPUT_LIMITS } from "../../input-limits";

export const ScrollToTopSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    icon = "arrow-up-short",
    customIcon,
    hasCustomIcon,
    size = "24px",
    color = "#ffffff",
    backgroundColor = "#16537e",
    hoverColor,
    hoverBackgroundColor,
    layoutPadding = {
      top: "8px",
      bottom: "8px",
      left: "8px",
      right: "8px",
    },
    layoutMargin,
    border,
    borderRadius = {
      bottomLeft: "100px",
      bottomRight: "100px",
      topLeft: "100px",
      topRight: "100px",
    },
    boxShadow,
    hoverLayoutPadding,
    hoverLayoutMargin,
    hoverBorder,
    hoverBorderRadius,
    hoverBoxShadow,
    position = "fixed",
    topPosition,
    topPositionTablet,
    topPositionMobile,
    bottomPosition = "50px",
    bottomPositionTablet,
    bottomPositionMobile,
    leftPosition,
    leftPositionTablet,
    leftPositionMobile,
    rightPosition = "30px",
    rightPositionTablet,
    rightPositionMobile,
    zindex = 100,
    offset = "400px",
    smoothScrolling = true,
    onlyShowWhenScrollingUp,
    transition,
  } = props;

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing={"1rem"}>
          <UnitInput
            resetValue={"400px"}
            value={offset}
            onChange={(val) => {
              setProp((props) => {
                props.offset = val;
              });
            }}
            label={"Show After Scrolling"}
          />
          <ToggleInput
            resetValue={undefined}
            value={smoothScrolling}
            onChange={(val) => {
              setProp((props) => {
                props.smoothScrolling = val;
              });
            }}
            label={"Smooth Scrolling"}
          />

          <ToggleInput
            resetValue={undefined}
            value={onlyShowWhenScrollingUp}
            onChange={(val) => {
              setProp((props) => {
                props.onlyShowWhenScrollingUp = val;
              });
            }}
            label={"Only Show When Scrolling Up"}
          />
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: icon, default: "arrow-up-short" },
            { key: hasCustomIcon },
            { key: customIcon },
            { key: size, default: "24px" },
          ])}
          id={`block-setting-${id}-icon`}
          title={"Icon"}
          initialOpen={true}
        >
          <IconInput
            resetValue={undefined}
            activeIcon={icon}
            setActiveIcon={(val) => {
              setProp((props) => {
                props.icon = val;
              });
            }}
            supportCustom
            hasCustomIcon={hasCustomIcon}
            setHasCustomIcon={(val) => {
              setProp((props) => {
                props.hasCustomIcon = val;
              });
            }}
            customIcon={customIcon}
            setCustomIcon={(val) => {
              setProp((props) => {
                props.customIcon = val;
              });
            }}
          />
          <UnitInput
            resetValue={"24px"}
            value={size}
            onChange={(val) => {
              setProp((props) => {
                props.size = val;
              });
            }}
            label={"Size"}
          />
        </Panel>

        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: position, default: "fixed" },
            { key: topPosition },
            { key: topPositionTablet },
            { key: topPositionMobile },
            { key: bottomPosition, default: "50px" },
            { key: bottomPositionTablet },
            { key: bottomPositionMobile },
            { key: leftPosition },
            { key: leftPositionTablet },
            { key: leftPositionMobile },
            { key: rightPosition, default: "30px" },
            { key: rightPositionTablet },
            { key: rightPositionMobile },
            { key: zindex, default: "100" },
          ])}
          id={`block-setting-${id}-position`}
          title={"Position"}
          initialOpen={true}
        >
          <SelectInput
            resetValue={"fixed"}
            value={position}
            onChange={(val) => {
              setProp((props) => {
                props.position = val;
              });
            }}
            options={[
              { label: "Fixed", value: "fixed" },
              { label: "Relative", value: "relative" },
              { label: "Static", value: "static" },
              { label: "Absolute", value: "absolute" },
              { label: "Sticky", value: "sticky" },
            ]}
            label="Position"
          />
          <UnitInput
            resetValue={undefined}
            value={topPosition}
            isResponsive={true}
            valueTablet={topPositionTablet}
            valueMobile={topPositionMobile}
            onChange={(val) => {
              setProp((props) => {
                props.topPosition = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.topPositionTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.topPositionMobile = val;
              });
            }}
            label={"Top"}
          />
          <UnitInput
            resetValue={"50px"}
            isResponsive={true}
            value={bottomPosition}
            valueTablet={bottomPositionTablet}
            valueMobile={bottomPositionMobile}
            onChange={(val) => {
              setProp((props) => {
                props.bottomPosition = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.bottomPositionTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.bottomPositionMobile = val;
              });
            }}
            label={"Bottom"}
          />
          <UnitInput
            resetValue={undefined}
            isResponsive={true}
            value={leftPosition}
            valueTablet={leftPositionTablet}
            valueMobile={leftPositionMobile}
            onChange={(val) => {
              setProp((props) => {
                props.leftPosition = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.leftPositionTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.leftPositionMobile = val;
              });
            }}
            label={"Left"}
          />
          <UnitInput
            resetValue={"30px"}
            isResponsive={true}
            value={rightPosition}
            valueTablet={rightPositionTablet}
            valueMobile={rightPositionMobile}
            onChange={(val) => {
              setProp((props) => {
                props.rightPosition = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.rightPositionTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.rightPositionMobile = val;
              });
            }}
            label={"Right"}
          />
          <NumberInput
            // min={0}
            // max={100000}
            min={INPUT_LIMITS["scroll-to-top-zindex"].min}
            max={INPUT_LIMITS["scroll-to-top-zindex"].max}
            step={INPUT_LIMITS["scroll-to-top-zindex"].step}
            resetValue={100}
            value={zindex}
            onChange={(val) => {
              setProp((props) => {
                props.zindex = val;
              });
            }}
            label={"Z Index"}
          />
        </Panel>
      </div>
    );
  }

  function StyleSettings() {
    const colorTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalSettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverSettings(),
      },
    ];
    function normalSettings() {
      return (
        <>
          {" "}
          <ColorInput
            resetValue={"#fff"}
            value={color}
            onChange={(val) => {
              setProp((props) => {
                props.color = val;
              });
            }}
            label={"Icon"}
          />
          <ColorInput
            resetValue={"#16537e"}
            value={backgroundColor}
            onChange={(val) => {
              setProp((props) => {
                props.backgroundColor = val;
              });
            }}
            label={"Background"}
          />
        </>
      );
    }
    function hoverSettings() {
      return (
        <>
          {" "}
          <ColorInput
            resetValue={"#fff"}
            value={hoverColor}
            onChange={(val) => {
              setProp((props) => {
                props.hoverColor = val;
              });
            }}
            label={"Icon"}
          />
          <ColorInput
            resetValue={"#16537e"}
            value={hoverBackgroundColor}
            onChange={(val) => {
              setProp((props) => {
                props.hoverBackgroundColor = val;
              });
            }}
            label={"Background"}
          />
          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition?.types?.[("color", "background-color")]?.duration}
            onChange={(val) => {
              changeTransition(
                ["color", "background-color"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }
    return (
      <>
        <Spacer spacing={"1rem"}>
          <TabsV2 items={colorTabs} />
        </Spacer>
      </>
    );
  }

  function AdvancedSettings() {
    const marginPaddingTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalMarginPaddingSettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverMarginPaddingSettings(),
      },
    ];

    function normalMarginPaddingSettings() {
      return (
        <>
          <MarginInput
            isResponsive={true}
            value={layoutMargin}
            onChange={(val) => {
              setProp((props) => {
                props.layoutMargin = val;
              });
            }}
            label={"Margin"}
          />
          <PaddingInput
            resetValue={{
              top: "8px",
              bottom: "8px",
              left: "8px",
              right: "8px",
            }}
            isResponsive={true}
            value={layoutPadding}
            onChange={(val) => {
              setProp((props) => {
                props.layoutPadding = val;
              });
            }}
            label={"Padding"}
          />
        </>
      );
    }
    function hoverMarginPaddingSettings() {
      return (
        <>
          <MarginInput
            isResponsive={true}
            value={hoverLayoutMargin}
            onChange={(val) => {
              setProp((props) => {
                props.hoverLayoutMargin = val;
              });
            }}
            label={"Margin"}
          />
          <PaddingInput
            isResponsive={true}
            value={hoverLayoutPadding}
            onChange={(val) => {
              setProp((props) => {
                props.hoverLayoutPadding = val;
              });
            }}
            label={"Padding"}
          />
          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition?.types?.[("margin", "padding")]?.duration}
            onChange={(val) => {
              changeTransition(
                ["margin", "padding"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }
    const borderTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalBorderSettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverBorderSettings(),
      },
    ];

    function normalBorderSettings() {
      return (
        <>
          <BorderInput
            resetValue={undefined}
            value={border}
            onChange={(val) => {
              setProp((props) => {
                props.border = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            resetValue={{
              bottomLeft: "100px",
              bottomRight: "100px",
              topLeft: "100px",
              topRight: "100px",
            }}
            value={borderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.borderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
        </>
      );
    }
    function hoverBorderSettings() {
      return (
        <>
          <BorderInput
            resetValue={undefined}
            value={hoverBorder}
            onChange={(val) => {
              setProp((props) => {
                props.hoverBorder = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={hoverBorderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.hoverBorderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition?.types?.["border"]?.duration}
            onChange={(val) => {
              changeTransition(
                ["border"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }
    const boxShadowTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalBoxShadowSettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverBoxShadowSettings(),
      },
    ];

    function normalBoxShadowSettings() {
      return (
        <>
          <BoxShadowInput
            resetValue={undefined}
            value={boxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.boxShadow = val;
              });
            }}
          />
        </>
      );
    }
    function hoverBoxShadowSettings() {
      return (
        <>
          <BoxShadowInput
            resetValue={undefined}
            value={hoverBoxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.hoverBoxShadow = val;
              });
            }}
          />
          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition?.types?.["box-shadow"]?.duration}
            onChange={(val) => {
              changeTransition(
                ["box-shadow"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutMargin },
            {
              key: layoutPadding,
              default: {
                top: "8px",
                bottom: "8px",
                left: "8px",
                right: "8px",
              },
            },
            { key: hoverLayoutMargin },
            { key: hoverLayoutPadding },
          ])}
          id={`block-setting-${id}-layout`}
          title={"Layout"}
          initialOpen={true}
        >
          <TabsV2 items={marginPaddingTabs} />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: border },
            {
              key: borderRadius,
              default: {
                bottomLeft: "100px",
                bottomRight: "100px",
                topLeft: "100px",
                topRight: "100px",
              },
            },
            { key: hoverBorder },
            { key: hoverBorderRadius },
          ])}
          id={`block-setting-${id}-border`}
          title={"Border"}
          initialOpen={true}
        >
          <TabsV2 items={borderTabs} />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: boxShadow },
            { key: hoverBoxShadow },
          ])}
          id={`block-setting-${id}-box-shadow`}
          title={"Box Shadow"}
          initialOpen={true}
        >
          <TabsV2 items={boxShadowTabs} />
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: offset },
        { key: smoothScrolling },
        { key: onlyShowWhenScrollingUp },
        { key: icon, default: "arrow-up-short" },
        { key: hasCustomIcon },
        { key: customIcon },
        { key: size, default: "24px" },
        { key: position, default: "fixed" },
        { key: topPosition },
        { key: topPositionTablet },
        { key: topPositionMobile },
        { key: bottomPosition, default: "50px" },
        { key: bottomPositionTablet },
        { key: bottomPositionMobile },
        { key: leftPosition },
        { key: leftPositionTablet },
        { key: leftPositionMobile },
        { key: rightPosition, default: "30px" },
        { key: rightPositionTablet },
        { key: rightPositionMobile },
        { key: zindex, default: "100" },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: color },
        { key: backgroundColor },
        { key: hoverColor },
        { key: hoverBackgroundColor },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layoutMargin },
        {
          key: layoutPadding,
          default: {
            top: "8px",
            bottom: "8px",
            left: "8px",
            right: "8px",
          },
        },
        { key: border },
        {
          key: borderRadius,
          default: {
            bottomLeft: "100px",
            bottomRight: "100px",
            topLeft: "100px",
            topRight: "100px",
          },
        },
        { key: boxShadow },
        { key: hoverBoxShadow },
        { key: hoverBorder },
        { key: hoverBorderRadius },
        { key: hoverLayoutMargin },
        { key: hoverLayoutPadding },
      ]),
    },
  ];
  return (
    <div>
      <Tabs
        type="block-setting"
        id={`block-settings-tab-${id}`}
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
